import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, field, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from "uuid";
import { Address } from './addresses/Address';
import { Group } from './Group';
import { GroupCategorySettings, GroupCategoryTree } from './GroupCategory';
import { OrganizationMetaData } from './OrganizationMetaData';
import { OrganizationPrivateMetaData } from './OrganizationPrivateMetaData';
import { User } from './User';
import { Webshop, WebshopPreview } from './webshops/Webshop';
export class Organization extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.website = null;
        this.registerDomain = null;
        /**
         * All the available groups are listed here. They are only 'active' and visible when inside a category. Please remove them here if they are inactive.
         * Deleting a group will also trigger database deletion.
         */
        this.groups = [];
        /**
         * Only set for users with full access to the organization
         */
        this.privateMeta = null;
        /**
         * Only set for users with full access to the organization
         */
        this.webshops = [];
    }
    /**
     * Get all groups that are in a category
     */
    get availableGroups() {
        return this.categoryTree.getAllGroups();
    }
    getGroupsForPermissions(permissions) {
        return this.getCategoryTree({ permissions }).getAllGroups();
    }
    get adminAvailableGroups() {
        return this.adminCategoryTree.getAllGroups();
    }
    /**
     * Get all groups that are in a category
     */
    get availableCategories() {
        return this.adminCategoryTree.getAllCategories();
    }
    /**
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    get categoryTree() {
        return this.getCategoryTree();
    }
    get publicCategoryTree() {
        return this.getCategoryTree({ smartCombine: true });
    }
    get adminCategoryTree() {
        return this.getCategoryTree({ admin: true });
    }
    isAcceptingNewMembers(admin) {
        const allGroups = this.adminAvailableGroups; // we need to check admin groups because required groups could be restricted to internal groups
        const groups = this.getCategoryTree({ admin }).getAllGroups();
        for (const group of groups) {
            if (group.closed && !group.notYetOpen) {
                continue;
            }
            if (group.settings.requireGroupIds.length > 0 && group.settings.requireGroupIds.find(id => !!allGroups.find(g => g.id === id))) {
                continue;
            }
            if (group.settings.requirePreviousGroupIds.length > 0 && group.settings.requirePreviousGroupIds.find(id => !!allGroups.find(g => g.id === id))) {
                continue;
            }
            if (group.settings.availableMembers === 0 && !group.settings.waitingListIfFull) {
                continue;
            }
            return true;
        }
        return false;
    }
    isAcceptingExistingMembers(admin) {
        const groups = this.getCategoryTree({ admin }).getAllGroups();
        for (const group of groups) {
            if (group.closed) {
                continue;
            }
            if (group.settings.availableMembers === 0 && !group.settings.waitingListIfFull) {
                continue;
            }
            return true;
        }
        return false;
    }
    /**
     * @deprecated
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    categoryTreeForPermissions(permissions) {
        return this.getCategoryTree({ permissions });
    }
    /**
     * Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     *
     * For registration members perspective, try to use options.admin instead of options.permissions.
     * options.permissions is only used if you want to hide groups and empty categories that you don't have permissions for.
     */
    getCategoryTree(options) {
        var _a;
        const root = this.meta.categories.find(c => c.id === this.meta.rootCategoryId);
        if (root) {
            let tree = GroupCategoryTree.build(root, this, {
                groups: (options === null || options === void 0 ? void 0 : options.filterGroups) ? this.groups.filter(options.filterGroups) : undefined,
                permissions: options === null || options === void 0 ? void 0 : options.permissions,
                maxDepth: options === null || options === void 0 ? void 0 : options.maxDepth,
                smartCombine: options === null || options === void 0 ? void 0 : options.smartCombine
            });
            if (!(options === null || options === void 0 ? void 0 : options.permissions)) {
                // Hide non public items
                tree = tree.filterForDisplay((_a = options === null || options === void 0 ? void 0 : options.admin) !== null && _a !== void 0 ? _a : false, this.meta.packages.useActivities || (options === null || options === void 0 ? void 0 : options.admin), options === null || options === void 0 ? void 0 : options.smartCombine);
            }
            if (tree.categories.length == 0 && tree.groups.length > 0) {
                tree.settings.name = "Inschrijvingsgroepen";
                return GroupCategoryTree.create({
                    settings: GroupCategorySettings.create({
                        name: ""
                    }),
                    categories: [tree]
                });
            }
            return tree;
        }
        // Broken setup here
        console.warn("Root category ID is missing in categories. Migration might be needed");
        return GroupCategoryTree.create({});
    }
    /**
     * @deprecated
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    getCategoryTreeWithDepth(maxDepth) {
        return this.getCategoryTree({ maxDepth });
    }
    isCategoryDeactivated(category) {
        if (this.meta.packages.useActivities) {
            return false;
        }
        const cleanedTree = this.getCategoryTree({ maxDepth: 1 });
        if (cleanedTree.categories.find(c => c.id === category.id)) {
            return false;
        }
        return true;
    }
    get resolvedRegisterDomain() {
        var _a;
        if (this.registerDomain) {
            return this.registerDomain;
        }
        return this.uri + '.' + ((_a = STAMHOOFD.domains.registration[this.address.country]) !== null && _a !== void 0 ? _a : STAMHOOFD.domains.registration[""]);
    }
    get registerUrl() {
        return "https://" + this.resolvedRegisterDomain;
    }
    get dashboardDomain() {
        return STAMHOOFD.domains.dashboard;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Organization.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Organization.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], Organization.prototype, "website", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], Organization.prototype, "registerDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 3, upgrade: () => "" })
], Organization.prototype, "uri", void 0);
__decorate([
    field({ decoder: OrganizationMetaData, defaultValue: () => OrganizationMetaData.create({}) })
], Organization.prototype, "meta", void 0);
__decorate([
    field({ decoder: Address })
], Organization.prototype, "address", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), version: 2, upgrade: () => [], optional: true })
], Organization.prototype, "groups", void 0);
__decorate([
    field({ decoder: OrganizationPrivateMetaData, nullable: true, version: 6 })
], Organization.prototype, "privateMeta", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 38, upgrade: () => [] })
], Organization.prototype, "webshops", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(User), optional: true, version: 60 })
], Organization.prototype, "admins", void 0);
export class OrganizationWithWebshop extends AutoEncoder {
}
__decorate([
    field({ decoder: Organization })
], OrganizationWithWebshop.prototype, "organization", void 0);
__decorate([
    field({ decoder: Webshop })
], OrganizationWithWebshop.prototype, "webshop", void 0);
export class GetWebshopFromDomainResult extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * There is one specific webshop for the given domain.
         */
        this.webshop = null;
        /**
         * Multiple webshops possible. Show a selection page.
         */
        this.webshops = [];
    }
}
__decorate([
    field({ decoder: Organization })
], GetWebshopFromDomainResult.prototype, "organization", void 0);
__decorate([
    field({ decoder: Webshop, nullable: true })
], GetWebshopFromDomainResult.prototype, "webshop", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 137 })
], GetWebshopFromDomainResult.prototype, "webshops", void 0);
export const OrganizationPatch = Organization.patchType();
//# sourceMappingURL=Organization.js.map